// src/components/ProfileEditCard.jsx
import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUserInfo } from "store/slices/userSlice";

import api from "api/api";
import endpoint from "api/endpoints";
import {
  InputField,
  Button,
  AddressModal,
  FormSubmit,
  ErrorMessage,
  Loading,
  Toast,
} from "common";

import { validatePhone, generateRandomPassword } from "../../config/config";

import { PhoneInputField, formatPhone } from "@/design-system";

// Restyles PhoneInputField to match the InputField look of the name fields.
// `> div` is TextField's bordered wrapper around the input.
const LEGACY_FIELD_LOOK = [
  "mb-2 !gap-1 [&>label]:!font-medium",
  "[&>div]:!h-10 [&>div]:!px-3 [&>div]:!rounded [&>div]:!border",
  "[&>div]:!border-gray-200 [&>div]:!shadow-none",
  "[&>div:focus-within]:ring-2 [&>div:focus-within]:ring-smiles-orange",
  "[&>div:has([aria-invalid])]:!border-red-500",
  "[&>div:has([aria-invalid]):focus-within]:ring-red-400",
].join(" ");

export default function ProfileEditCard({ onClose, error }) {
  const { user, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const customer = useSelector((state) => state.user.info);

  const [formData, setFormData] = useState({
    firstName: customer?.firstname || "",
    lastName: customer?.lastname || "",
    homePhone: formatPhone(customer?.homePhone),
    mobilePhone: formatPhone(customer?.mobilePhone),
  });

  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleChange = (e) => updateField(e.target.name, e.target.value);

  const namesFilled = formData.firstName.trim() && formData.lastName.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!validatePhone(formData.homePhone)) {
      newErrors.homePhone =
        "Enter a valid 10-digit home phone, or leave it blank.";
    }
    if (!validatePhone(formData.mobilePhone)) {
      newErrors.mobilePhone =
        "Enter a valid 10-digit mobile phone, or leave it blank.";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Generate a random secure password since Auth0 handles authentication
    const randomPassword = generateRandomPassword();

    try {
      setSubmitting(true);
      const token = await getAccessTokenSilently();

      // Check if customer already exists
      try {
        const existingCustomerResponse = await api.get(
          endpoint.GET_CUSTOMER_BY_EMAIL(user.email),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (
          existingCustomerResponse.data &&
          existingCustomerResponse.data.length > 0
        ) {
          setErrors({ form: "An account with this email already exists." });
          Toast.error("An account with this email already exists.");
          return;
        }
      } catch (checkError) {}

      await api.post(
        endpoint.POST_CREATE_CUSTOMER(),
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          homePhone: formData.homePhone,
          mobilePhone: formData.mobilePhone,
          email: user.email,
          custentity1: user.email,
          entityStatus: { id: 6 },
          subsidiary: { id: 2 },
          category: { id: 15 },
          isPerson: true,
          giveAccess: true,
          password: randomPassword,
          password2: randomPassword,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(fetchUserInfo({ user, getAccessTokenSilently }));
      Toast.success("Profile created successfully!");
      if (onClose) onClose();
      navigate("/");
    } catch (err) {
      setErrors({ form: "Failed to create profile." });
      Toast.error("Failed to create profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Profile Information</h3>
      {submitting && <Loading />}
      <FormSubmit onSubmit={handleSubmit}>
        {error && <ErrorMessage message={error} />}
        {errors.form && <ErrorMessage message={errors.form} />}
        <InputField
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
        <InputField
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
        <PhoneInputField
          label="Home Phone"
          name="homePhone"
          value={formData.homePhone}
          onChange={(phone) => updateField("homePhone", phone)}
          error={errors?.homePhone}
          className={LEGACY_FIELD_LOOK}
        />
        <PhoneInputField
          label="Mobile Phone"
          name="mobilePhone"
          value={formData.mobilePhone}
          onChange={(phone) => updateField("mobilePhone", phone)}
          error={errors?.mobilePhone}
          className={LEGACY_FIELD_LOOK}
        />

        <div className="mt-2 text-sm">
          <span>Email</span>
          <br />
          <span className="text-gray-700">{user?.email}</span>
        </div>

        <div className="mt-4 flex gap-4">
          <Button type="submit" disabled={!namesFilled || submitting}>
            Create
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </FormSubmit>

      {showModal && <AddressModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
